import { createReducer, on } from '@ngrx/store';
import { NavigationState } from '../states/navigation.state';
import { ReportMenu } from '../../entities/report-menu.entity';
import * as NavigationActions from '../actions/navigation.actions';

const STORAGE_ROUTES_KEY = 'powerbiNavigationRoutes';
const STORAGE_SELECTED_ROUTE_KEY = 'powerbiSelectedRoute';

const INVALID_STORAGE_LITERALS = new Set(['undefined', 'null']);

function safeParseRoutes(raw: string | null): ReportMenu[] | null {
  if (raw == null || raw === '' || INVALID_STORAGE_LITERALS.has(raw.trim())) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ReportMenu[]) : null;
  } catch {
    return null;
  }
}

function clearLegacyPowerBiNavigationStorage(): void {
  localStorage.removeItem('routes');
  localStorage.removeItem('selectedRoute');
}

clearLegacyPowerBiNavigationStorage();

function getRoutesFromStorage(): ReportMenu[] {
  const raw = localStorage.getItem(STORAGE_ROUTES_KEY);
  const routes = safeParseRoutes(raw);
  if (routes === null) {
    if (raw !== null) {
      localStorage.removeItem(STORAGE_ROUTES_KEY);
    }
    return [];
  }
  return routes;
}

function getSelectedRouteFromStorage(): ReportMenu | null {
  const storedRoute = localStorage.getItem(STORAGE_SELECTED_ROUTE_KEY);
  if (!storedRoute || INVALID_STORAGE_LITERALS.has(storedRoute.trim())) {
    if (storedRoute !== null) {
      localStorage.removeItem(STORAGE_SELECTED_ROUTE_KEY);
    }
    return null;
  }
  try {
    return JSON.parse(storedRoute) as ReportMenu;
  } catch (error) {
    console.error('Error parsing stored Power BI selected route:', error);
    localStorage.removeItem(STORAGE_SELECTED_ROUTE_KEY);
    return null;
  }
}

export const initialState: NavigationState = {
  routes: getRoutesFromStorage(),
  selectedRoute: getSelectedRouteFromStorage(),
};

// reducer
export const navigationReducer = createReducer(
  initialState,
  on(NavigationActions.loadNavigationSuccess, (state, { routes }) => {
    const safeRoutes = Array.isArray(routes) ? routes : [];
    localStorage.setItem(STORAGE_ROUTES_KEY, JSON.stringify(safeRoutes));

    if (state.selectedRoute) {
      const routeStillExists = safeRoutes.some(
        (route: ReportMenu) => route.uuid === state.selectedRoute?.uuid,
      );
      if (!routeStillExists) {
        localStorage.removeItem(STORAGE_SELECTED_ROUTE_KEY);
        return { ...state, routes: safeRoutes, selectedRoute: null };
      }
    }

    return { ...state, routes: safeRoutes };
  }),
  on(NavigationActions.loadNavigationFailure, (state) => ({
    ...state,
  })),
  on(NavigationActions.selectRoute, (state, { route }) => {
    localStorage.setItem(STORAGE_SELECTED_ROUTE_KEY, JSON.stringify(route));
    return {
      ...state,
      selectedRoute: route,
    };
  }),
  on(NavigationActions.deselectRoute, (state) => {
    localStorage.removeItem(STORAGE_SELECTED_ROUTE_KEY);
    return {
      ...state,
      selectedRoute: null,
    };
  }),
);
